import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ShoppingCart, Menu, Search } from 'lucide-react';
import { useCart } from './hooks/useCart';
import { useStore } from './hooks/useStore';

export default function StoreLayout({ children }: { children: React.ReactNode }) {
  const { items } = useCart();
  const { settings, loading } = useStore();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const location = useLocation();
  const { storeSlug } = useParams();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      window.location.href = `/shop/${storeSlug}/search?q=${encodeURIComponent(searchTerm)}`;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      {/* Top Bar */}
      <div className="bg-[#0F172A] text-white py-2">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <span className="text-sm">Bem-vindo à {settings.storeName}</span>
            <div className="flex items-center space-x-4">
              <Link to={`/shop/${storeSlug}/contact`} className="text-sm hover:text-gray-300">Contacto</Link>
              <Link to={`/shop/${storeSlug}/about`} className="text-sm hover:text-gray-300">Sobre nós</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to={`/shop/${storeSlug}`} className="flex items-center">
              <img
                src="/logo_lognow_preto.svg"
                alt={settings.storeName}
                className="h-8 w-auto"
              />
            </Link>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl mx-8">
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="Pesquisar produtos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-[#C5D86D] focus:border-[#C5D86D]"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              </div>
            </form>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to={`/shop/${storeSlug}`} className={`text-[#0F172A] hover:text-[#C5D86D] ${location.pathname === `/shop/${storeSlug}` ? 'font-bold' : ''}`}>
                Início
              </Link>
              <Link to={`/shop/${storeSlug}/products`} className={`text-[#0F172A] hover:text-[#C5D86D] ${location.pathname === `/shop/${storeSlug}/products` ? 'font-bold' : ''}`}>
                Produtos
              </Link>
              <Link to={`/shop/${storeSlug}/cart`} className="relative text-[#0F172A] hover:text-[#C5D86D]">
                <ShoppingCart className="h-6 w-6" />
                {items.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#C5D86D] text-[#0F172A] text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {items.length}
                  </span>
                )}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-[#0F172A]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-4">
              <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Pesquisar produtos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-[#C5D86D] focus:border-[#C5D86D]"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
              </form>
              <Link
                to={`/shop/${storeSlug}`}
                className="block text-[#0F172A] hover:text-[#C5D86D]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Início
              </Link>
              <Link
                to={`/shop/${storeSlug}/products`}
                className="block text-[#0F172A] hover:text-[#C5D86D]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Produtos
              </Link>
              <Link
                to={`/shop/${storeSlug}/contact`}
                className="block text-[#0F172A] hover:text-[#C5D86D]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contacto
              </Link>
              <Link
                to={`/shop/${storeSlug}/cart`}
                className="block text-[#0F172A] hover:text-[#C5D86D]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Carrinho ({items.length})
              </Link>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow container mx-auto px-4 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-bold mb-4">Sobre Nós</h3>
              <p className="text-gray-300">{settings.description}</p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4">Links Úteis</h3>
              <ul className="space-y-2">
                <li>
                  <Link to={`/shop/${storeSlug}/products`} className="text-gray-300 hover:text-[#C5D86D]">
                    Produtos
                  </Link>
                </li>
                <li>
                  <Link to={`/shop/${storeSlug}/contact`} className="text-gray-300 hover:text-[#C5D86D]">
                    Contacto
                  </Link>
                </li>
                <li>
                  <Link to={`/shop/${storeSlug}/cart`} className="text-gray-300 hover:text-[#C5D86D]">
                    Carrinho
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4">Contactos</h3>
              <ul className="space-y-2 text-gray-300">
                {settings.phone && (
                  <li>Telefone: {settings.phone}</li>
                )}
                {settings.email && (
                  <li>Email: {settings.email}</li>
                )}
                {settings.address && (
                  <li>Morada: {settings.address}</li>
                )}
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-300">
            <p> {new Date().getFullYear()} {settings.storeName}. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
